import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { FeatureCollection, Feature, Geometry } from 'geojson';

interface RegionFocusHandlerProps {
  geoData: FeatureCollection | null;
  focusedRegions?: string[];
  defaultCenter: [number, number];
  defaultZoom: number;
}

function flyToFeatures(
  map: L.Map,
  geoData: FeatureCollection,
  names: string[]
) {
  const matching = geoData.features.filter((f) => {
    const name = f.properties?.name as string | undefined;
    return name ? names.includes(name) : false;
  });
  if (matching.length === 0) return;
  const layer = L.geoJSON(matching as Feature<Geometry>[]);
  const bounds = layer.getBounds();
  if (!bounds.isValid()) return;
  map.flyToBounds(bounds, {
    padding: [80, 80],
    duration: 0.9,
    easeLinearity: 0.15,
    maxZoom: 11,
  });
}

export function RegionFocusHandler({
  geoData,
  focusedRegions,
  defaultCenter,
  defaultZoom,
}: RegionFocusHandlerProps) {
  const map = useMap();
  const prevRegionsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (!geoData) return;
    const regions = focusedRegions ?? [];
    const prev = prevRegionsRef.current;
    prevRegionsRef.current = regions;

    if (prev === null) return;

    const prevSet = new Set(prev);
    const sameSet =
      prev.length === regions.length && regions.every((r) => prevSet.has(r));
    if (sameSet) return;

    if (regions.length === 0) {
      map.flyTo(defaultCenter, defaultZoom, {
        duration: 0.9,
        easeLinearity: 0.15,
      });
      return;
    }

    const added = regions.filter((r) => !prevSet.has(r));
    const target = added.length === 1 ? added : regions;
    flyToFeatures(map, geoData, target);
  }, [focusedRegions, geoData, map, defaultCenter, defaultZoom]);

  return null;
}
